/**
 * Error message helper for authentication and API responses
 */

/**
 * Get user-friendly error message based on response status and data
 * @param {number} status - HTTP status code
 * @param {Object} errorData - Error data from response
 * @returns {string} User-friendly error message
 */
export const getAuthErrorMessage = (status, errorData) => {
  const message = errorData?.message || '';
  
  // Backend now sends messages in Spanish, so we can use them directly or check for Spanish messages
  // Check for specific error messages first (regardless of status code)
  // Email already exists (can come as 409 or 400)
  if (message.includes('ya está registrado') || message.includes('already exists') || message.includes('already registered')) {
    return message || 'Este email ya está registrado. Por favor, inicia sesión o utiliza otro email.';
  }
  
  if (message.includes('Error de conexión con la base de datos') || message.includes('Database error occurred')) {
    return 'Error de conexión con la base de datos. Por favor, intenta nuevamente en unos momentos.';
  }
  
  if (message.includes('Error en la configuración de tu cuenta') || message.includes('Account configuration error')) {
    return 'Error en la configuración de tu cuenta. Por favor, contacta al soporte.';
  }
  
  if (message.includes('Error al verificar tu contraseña') || message.includes('Authentication error occurred')) {
    return 'Error al verificar tu contraseña. Por favor, intenta nuevamente.';
  }
  
  if (message.includes('Error al verificar el código de autenticación') || message.includes('MFA verification error')) {
    return 'Error al verificar el código de autenticación. Por favor, intenta nuevamente.';
  }
  
  if (message.includes('Error al generar el token de acceso') || message.includes('Token generation failed')) {
    return 'Error al generar el token de acceso. Por favor, intenta nuevamente.';
  }
  
  if (message.includes('Error en la configuración del servidor') || message.includes('Server configuration error')) {
    return 'Error en la configuración del servidor. Por favor, contacta al soporte.';
  }
  
  if (message.includes('Email y contraseña son requeridos') || message.includes('Email and password are required')) {
    return 'Por favor, completa todos los campos requeridos.';
  }

  const errorMessages = {
    400: () => {
      if (message.includes('Email y contraseña son requeridos') || message.includes('Email and password are required')) {
        return 'Por favor, completa todos los campos requeridos.';
      }
      // For validation errors, use the details field if available for more context
      if (errorData?.details) {
        // If details is a string (formatted list), use it
        if (typeof errorData.details === 'string') {
          return errorData.details;
        }
        // If details is an array, format it nicely
        if (Array.isArray(errorData.details) && errorData.details.length > 0) {
          return errorData.details.map(e => `• ${e.message || e.msg || e}`).join('\n');
        }
      }
      // Backend messages are now in Spanish, use them directly
      return message || 'Datos inválidos. Por favor, verifica la información ingresada.';
    },
    
    401: () => {
      // Check for specific user not found message
      if (message.includes('El usuario no existe')) {
        return message; // Use backend message directly
      }
      // Check for invalid credentials (wrong password)
      if (message.includes('Credenciales inválidas') || message.includes('Credenciales invalidos') || message.includes('Invalid credentials')) {
        const attemptsRemaining = errorData?.data?.attemptsRemaining;
        if (attemptsRemaining !== undefined && attemptsRemaining >= 0) {
          return `Email o contraseña incorrectos. Intentos restantes: ${attemptsRemaining}.`;
        }
        return 'Email o contraseña incorrectos. Verifica tus datos e intenta nuevamente.';
      }
      // Use backend message directly (now in Spanish)
      return message || 'Credenciales inválidas. Por favor, verifica tu email y contraseña.';
    },
    
    403: () => {
      if (errorData?.requiresMFA) {
        return 'Autenticación de dos factores requerida. Por favor, ingresa el código de verificación.';
      }
      if (message.includes('Código de autenticación inválido') || message.includes('Invalid MFA token')) {
        return 'Código de autenticación inválido. Por favor, intenta nuevamente.';
      }
      // Use backend message directly (now in Spanish)
      return message || 'Acceso denegado. Contacta al administrador.';
    },
    
    423: () => {
      // Backend message is already in Spanish
      if (message.includes('Cuenta temporalmente bloqueada')) {
        const remainingTime = errorData?.data?.remainingTime;
        const minutes = remainingTime ? Math.ceil(remainingTime / 60000) : null;
        
        if (minutes) {
          return `Cuenta temporalmente bloqueada debido a múltiples intentos fallidos. Intenta nuevamente en ${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}.`;
        }
        return message;
      }
      return message || 'Cuenta temporalmente bloqueada. Intenta nuevamente más tarde.';
    },
    
    429: () => 'Demasiados intentos. Espera unos minutos antes de intentar nuevamente.',
    
    409: () => {
      // Handle email already exists and other conflict errors
      if (message.includes('ya está registrado') || message.includes('already exists') || message.includes('already registered')) {
        return message || 'Este email ya está registrado. Por favor, inicia sesión o utiliza otro email.';
      }
      // Use backend message directly (now in Spanish)
      return message || 'Conflicto con los datos proporcionados. Por favor, verifica la información.';
    },
    
    500: () => {
      // Backend messages are now in Spanish, use them directly
      if (message.includes('Ocurrió un error inesperado') || message.includes('unexpected error')) {
        return 'Ocurrió un error inesperado. Por favor, intenta nuevamente más tarde.';
      }
      // Use backend message directly (now in Spanish)
      return message || 'Error del servidor. Intenta nuevamente más tarde.';
    },
    
    default: () => {
      // Backend messages are now in Spanish, use them directly
      if (message) {
        return message;
      }
      return 'Error del servidor. Intenta nuevamente más tarde.';
    }
  };

  const getMessage = errorMessages[status] || errorMessages.default;
  return getMessage();
};

/**
 * Log error details for debugging
 * @param {Response} response - Fetch response object
 * @param {Object} errorData - Error data from response
 */
export const logAuthError = (response, errorData) => {
  console.log('🔍 Login Error Details:');
};
